import { useEffect, useState } from 'react';

import { useDotloopComplianceGroupsLazyQuery } from '@client/graphql/__generated__/document-operations';
import { DotloopComplianceGroupsQuery } from '@client/graphql/__generated__/types';

interface DotloopGroup {
  id: string;
  name: string;
  active: boolean;
}

export interface DotloopGroupSection {
  teamName: string;
  groups: DotloopGroup[];
}

const normalizeDotloopGroups = (
  raw: DotloopComplianceGroupsQuery['dotloopComplianceGroups']
): DotloopGroup[] => {
  const byId = new Map<string, DotloopGroup>();

  raw.forEach((group) => {
    const id = group?.id?.trim();
    const name = group?.name?.trim();
    if (!id || !name) return;

    const candidate: DotloopGroup = {
      id,
      name,
      active: Boolean(group.active),
    };

    const existing = byId.get(id);
    if (!existing) {
      byId.set(id, candidate);

      return;
    }

    if (!existing.active && candidate.active) {
      byId.set(id, candidate);
    }
  });

  return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
};

export const useDotloopComplianceGroupSections = (
  dotloopProfiles: Array<{ profileId: string; teamName: string }>
) => {
  const [fetchDotloopComplianceGroups] = useDotloopComplianceGroupsLazyQuery({
    fetchPolicy: 'cache-first',
  });

  const [dotloopGroupSections, setDotloopGroupSections] = useState<
    DotloopGroupSection[]
  >([]);
  const [loadingDotloopGroups, setLoadingDotloopGroups] = useState(false);
  const [dotloopGroupsLoaded, setDotloopGroupsLoaded] = useState(false);
  const [dotloopGroupsError, setDotloopGroupsError] = useState<
    string | undefined
  >(undefined);
  const [dotloopTeamsWithErrors, setDotloopTeamsWithErrors] = useState<
    string[]
  >([]);

  useEffect(() => {
    let cancelled = false;

    const loadDotloopGroups = async () => {
      if (dotloopProfiles.length === 0) {
        setDotloopGroupSections([]);
        setDotloopGroupsError(undefined);
        setDotloopTeamsWithErrors([]);
        setLoadingDotloopGroups(false);
        setDotloopGroupsLoaded(false);

        return;
      }

      setLoadingDotloopGroups(true);
      setDotloopGroupsLoaded(false);
      setDotloopGroupsError(undefined);
      setDotloopTeamsWithErrors([]);

      const sectionsByTeamName = new Map<string, Map<string, DotloopGroup>>();
      const teamOrder: string[] = [];
      const teamsWithErrors: string[] = [];
      let lastErrorMessage: string | undefined = undefined;

      for (const profile of dotloopProfiles) {
        try {
          const response = await fetchDotloopComplianceGroups({
            variables: {
              filters: {
                profileId: profile.profileId,
              },
            },
            fetchPolicy: 'cache-first',
          });

          if (cancelled) return;

          const groups = normalizeDotloopGroups(
            response.data?.dotloopComplianceGroups ?? []
          );
          const { teamName } = profile;

          if (!sectionsByTeamName.has(teamName)) {
            sectionsByTeamName.set(teamName, new Map());
            teamOrder.push(teamName);
          }

          const groupMap = sectionsByTeamName.get(teamName);
          if (!groupMap) continue;

          groups.forEach((group) => {
            const existing = groupMap.get(group.id);
            if (!existing) {
              groupMap.set(group.id, group);

              return;
            }

            if (!existing.active && group.active) {
              groupMap.set(group.id, group);
            }
          });
        } catch (error) {
          teamsWithErrors.push(profile.teamName);
          lastErrorMessage =
            error instanceof Error ? error.message : 'Unknown error';
        }
      }

      if (cancelled) return;

      const sections: DotloopGroupSection[] = teamOrder.map((teamName) => {
        const groupMap =
          sectionsByTeamName.get(teamName) ?? new Map<string, DotloopGroup>();
        const groups = Array.from(groupMap.values()).sort((a, b) =>
          a.name.localeCompare(b.name)
        );

        return { teamName, groups };
      });

      setDotloopTeamsWithErrors(teamsWithErrors);

      if (sections.length === 0 && teamsWithErrors.length > 0) {
        setDotloopGroupsError(
          lastErrorMessage ?? 'Failed to load Dotloop groups'
        );
      }

      setDotloopGroupSections(sections);
      setLoadingDotloopGroups(false);
      setDotloopGroupsLoaded(true);
    };

    void loadDotloopGroups();

    return () => {
      cancelled = true;
    };
  }, [dotloopProfiles, fetchDotloopComplianceGroups]);

  return {
    dotloopGroupSections,
    loadingDotloopGroups,
    dotloopGroupsLoaded,
    dotloopGroupsError,
    dotloopTeamsWithErrors,
  };
};
